import { usePublicConfig } from '@/hooks/queries/usePublicConfig';
import { useI18n } from '@/hooks/useI18n';
import { getLocalizedText, type I18nContent } from '@/utils/i18n';

/** Display fallbacks used before config loads or when a value is unset. */
export const DEFAULT_CONTACT_EMAIL = 'info@goloka.com';
export const DEFAULT_CONTACT_PHONE = '+603-1234 5678';

export interface ContactInfo {
  /** Contact email address (plain string). */
  email: string;
  /** Contact phone number as displayed (plain string). */
  phone: string;
  /** `tel:` href derived from the phone number (digits and leading `+` only). */
  phoneHref: string;
  /** Localized office address (may contain newlines). */
  address: string;
  /** Localized business hours (may contain newlines). */
  businessHours: string;
}

/** Parse a stored JSON I18nContent config value, tolerating malformed input. */
function parseI18nContent(value: string | undefined): I18nContent | null {
  if (!value) return null;
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' ? (parsed as I18nContent) : null;
  } catch {
    return null;
  }
}

/** Build a `tel:` href from a display phone number. */
function toTelHref(phone: string): string {
  const trimmed = phone.trim();
  const digits = trimmed.replace(/[^\d]/g, '');
  return trimmed.startsWith('+') ? `tel:+${digits}` : `tel:${digits}`;
}

/**
 * Read the admin-editable contact information from public config.
 *
 * Address and business hours are stored as multi-language JSON objects and are
 * resolved against the active language. Email and phone are plain strings.
 * i18n static strings (and hardcoded defaults) are used as fallbacks while the
 * config loads or when a value is empty.
 */
export function useContactInfo(): ContactInfo {
  const { t, lang } = useI18n();

  const { data: emailCfg } = usePublicConfig('contact.email');
  const { data: phoneCfg } = usePublicConfig('contact.phone');
  const { data: addressCfg } = usePublicConfig('contact.address');
  const { data: hoursCfg } = usePublicConfig('contact.business_hours');

  const email = emailCfg?.config_value?.trim() || DEFAULT_CONTACT_EMAIL;
  const phone = phoneCfg?.config_value?.trim() || DEFAULT_CONTACT_PHONE;

  const address = getLocalizedText(
    parseI18nContent(addressCfg?.config_value),
    lang,
    t('contact.addressContent'),
  );
  const businessHours = getLocalizedText(
    parseI18nContent(hoursCfg?.config_value),
    lang,
    t('contact.hoursContent'),
  );

  return {
    email,
    phone,
    phoneHref: toTelHref(phone),
    address,
    businessHours,
  };
}
